import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FullscreenButton } from "./FullscreenButton";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import {
  ArrowLeft,
  Download,
  AlertTriangle,
  Search,
  Eye,
  Heart,
  Wind,
  Activity,
  Smile,
  Brain,
  Pill,
  Ear,
  Lightbulb,
  Bone,
  Users,
  Leaf,
} from "lucide-react";

const CATEGORIES = [
  {
    id: 1,
    title: "OCULAR / EYES",
    range: "001–005",
    Icon: Eye,
    items: ["Clear Eyes", "Dry Eyes", "Eye Bleeding", "Tired Eyes", "Normal Skin"],
  },
  {
    id: 2,
    title: "DERMATOLOGY / SKIN",
    range: "006–013",
    Icon: Heart,
    items: [
      "Sunburn",
      "Freckles",
      "Dry Skin",
      "Minor Cuts",
      "Skin Rash",
      "Bruises",
      "Necrosis",
      "Dark aggressive abscesses (face)",
    ],
  },
  {
    id: 3,
    title: "RESPIRATORY",
    range: "014–019",
    Icon: Wind,
    items: [
      "Normal Breathing",
      "Light Snoring",
      "Seasonal Sniffles",
      "Hiccuping",
      "Persistent Cough",
      "Bloody Cough",
    ],
  },
  {
    id: 4,
    title: "VITALS / CARDIO-THERMAL",
    range: "020–024",
    Icon: Activity,
    items: [
      "Normal Temperature",
      "Normal Pulse",
      "Low Resting Pulse",
      "Elevated Temperature",
      "Critical Fever",
    ],
  },
  {
    id: 5,
    title: "ORAL / BREATH",
    range: "025–029",
    Icon: Smile,
    items: [
      "Fresh Breath",
      "Coffee Breath",
      "Garlic Breath",
      "Morning Breath",
      "Death-like Breath",
    ],
  },
  {
    id: 6,
    title: "BEHAVIOURAL / NEUROLOGICAL",
    range: "030–032",
    Icon: Brain,
    items: ["Alert Behavior", "Tired Behavior", "Anxious Behavior"],
  },
  {
    id: 7,
    title: "GASTROINTESTINAL / INTERNAL",
    range: "033–042",
    Icon: Pill,
    items: [
      "Nausea after fluorescein lighting",
      "Green/yellow bile in vomit",
      "Unexplained craving for mushrooms",
      "Refuses to eat orange foods",
      "Complains water tastes “too square”",
      "Sudden hiccups when hearing office printers",
      "Claims their stomach can predict rain",
      "Only eats food arranged by colour",
      "Reports “fizzy” internal organs",
      "Burps whenever someone says “containment”",
    ],
  },
  {
    id: 8,
    title: "SENSORY / AUDIO-VISUAL",
    range: "043–054",
    Icon: Ear,
    items: [
      "Low/high-frequency whistling / trilling",
      "Heightened hearing",
      "Sees spreadsheets when closing eyes",
      "Hears distant lift music that nobody else hears",
      "Unusual interest in buzzing electronics",
      "Claims certain vowels are physically itchy",
      "Cannot ignore blinking cursors",
      "Says the colour teal has a sound",
      "Overreacts to the sound of Velcro",
      "Becomes emotionally moved by photocopiers",
      "Mistakes applause for rainfall",
      "Insists silence is “too loud”",
    ],
  },
  {
    id: 9,
    title: "COGNITIVE / ATTENTION",
    range: "055–065",
    Icon: Lightbulb,
    items: [
      "Difficulty concentrating",
      "Confusion around simple instructions",
      "Counts ceiling tiles compulsively",
      "Takes notes in perfect alphabetical order",
      "Believes every acronym is a secret warning",
      "Forgets why they entered rooms, remembers irrelevant facts instead",
      "Treats clipboards as if they are emotionally significant",
      "Tries to rank objects by “trustworthiness”",
      "Attempts to interview inanimate objects",
      "Overexplains obvious things",
      "Convinced the mission acronym is speaking to them",
    ],
  },
  {
    id: 10,
    title: "MUSCULOSKELETAL / MOTOR",
    range: "066–076",
    Icon: Bone,
    items: [
      "Stiffness after sitting still",
      "Muscle twitch when hearing static",
      "Unusual posture near potted plants",
      "Walks in suspiciously perfect straight lines",
      "Cannot stop rotating pens between fingers",
      "Freezes briefly before entering doorways",
      "Compulsively adjusts sleeves before speaking",
      "Drums fingertips in complex repeating patterns",
      "Shoulder tension near fluorescent lights",
      "Sudden urge to crouch when alarm tones sound",
      "Treats ladders with reverence",
    ],
  },
  {
    id: 11,
    title: "SOCIAL / VERBAL / PERSONAL HABITS",
    range: "077–088",
    Icon: Users,
    items: [
      "Can’t help but quote popular song lyrics",
      "Refers to strangers as “research colleague”",
      "Laughs at entirely inappropriate moments",
      "Whispering becomes their default volume",
      "Answers questions with another question",
      "Greets rooms before people",
      "Refers to themselves in the plural on occasion",
      "Apologises defensively after bumping into objects",
      "Starts anecdotes but never finishes them",
      "Hums the same five-note sequence under their breath",
      "Uses the phrase “for science” too often",
      "Claps once before making important decisions",
    ],
  },
  {
    id: 12,
    title: "ENVIRONMENTAL / OBSERVABLE / PREFERENCES",
    range: "089–100",
    Icon: Leaf,
    items: [
      "Fringe orange colouration",
      "Obsession with wall-mounted plug sockets",
      "Loves the colour orange",
      "Rearranges objects into neat grids",
      "Unusual fondness for damp corners",
      "Keeps inspecting vents for “air politics”",
      "Distrusts revolving doors",
      "Emotionally attached to office stationery",
      "Insists some hallways feel “left-handed”",
      "Collects spare rubber bands without realising",
      "Regards magnifying glasses with awe",
      "Claims they can identify buildings by smell",
    ],
  },
];

// Build a flat numbered list for search + checked-count
const FLAT = (() => {
  const out = [];
  let n = 1;
  CATEGORIES.forEach((c) => {
    c.items.forEach((label) => {
      out.push({ num: n, categoryId: c.id, label });
      n += 1;
    });
  });
  return out;
})();

const STORAGE_KEY = "spore_index_checked";

const SporeIndex = () => {
  const navigate = useNavigate();
  const [checked, setChecked] = useState({});
  const [query, setQuery] = useState("");

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      if (saved && typeof saved === "object" && !Array.isArray(saved)) setChecked(saved);
    } catch {
      // ignore
    }
  }, []);

  const toggle = (num) => {
    setChecked((prev) => {
      const next = { ...prev, [num]: !prev[num] };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const clearAll = () => {
    setChecked({});
    localStorage.setItem(STORAGE_KEY, "{}");
  };

  const totalChecked = useMemo(
    () => Object.values(checked).filter(Boolean).length,
    [checked]
  );

  // Build a numbering map so each category knows its starting number
  const startIndex = useMemo(() => {
    const map = {};
    let n = 1;
    CATEGORIES.forEach((c) => {
      map[c.id] = n;
      n += c.items.length;
    });
    return map;
  }, []);

  const q = query.trim().toLowerCase();
  const matchesQuery = (num, label) =>
    !q || label.toLowerCase().includes(q) || String(num).padStart(3, "0").includes(q);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 text-slate-900">
      <div className="fixed top-4 right-4 z-50">
        <FullscreenButton variant="light" />
      </div>
      {/* Top bar */}
      <header className="bg-white border-b border-blue-200 shadow-sm">
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-baseline gap-1">
                <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-blue-900">
                  PROJECT SP
                </h1>
                <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-blue-500">
                  -ORE
                </h1>
              </div>
              <p className="text-sm text-slate-500 mt-1">
                Special Projects — Operational Risk and Environment
              </p>
              <div className="h-px bg-blue-200 my-3 max-w-md" />
              <h2 className="text-xl font-bold text-slate-800">EXPANDED SYMPTOM INDEX</h2>
              <p className="text-sm text-slate-500">
                Specialist-linked symptoms, core indicators and secondary behavioural markers (1–100).
              </p>
            </div>

            {/* Metadata card */}
            <div
              data-testid="spore-meta-card"
              className="bg-blue-50 border border-blue-200 rounded-lg px-5 py-4 text-sm min-w-[260px]"
            >
              <div className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1">
                <span className="text-slate-500">Reference:</span>
                <span className="font-medium text-slate-800">PS-SI-100</span>
                <span className="text-slate-500">Entries:</span>
                <span className="font-medium text-slate-800">100</span>
                <span className="text-slate-500">Categories:</span>
                <span className="font-medium text-slate-800">12</span>
                <span className="text-slate-500">Classification:</span>
                <span className="font-medium text-slate-800">Internal — Restricted</span>
              </div>
              <div className="h-px bg-blue-200 my-2" />
              <div className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1">
                <span className="text-slate-500">Subject:</span>
                <span className="font-medium text-slate-800">Research Programme</span>
                <span className="text-slate-500">Location:</span>
                <span className="font-medium text-slate-800">General Use</span>
              </div>
              <div className="mt-3 flex items-center gap-2 text-red-700 font-bold text-xs">
                <AlertTriangle className="h-4 w-4" />
                HAZARD CLASS: LETHAL BIOGENIC — OBSERVATION ONLY
              </div>
            </div>
          </div>

          {/* Toolbar */}
          <div className="mt-6 flex flex-wrap gap-3 items-center justify-between">
            <div className="flex gap-3 items-center flex-wrap">
              <Button
                data-testid="spore-back-btn"
                variant="outline"
                size="sm"
                onClick={() => navigate("/")}
              >
                <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
              </Button>
              <a
                href="/PROJECT_SPORE_Symptom_Index.pdf"
                target="_blank"
                rel="noreferrer"
                data-testid="spore-download-pdf"
              >
                <Button variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" /> Download PDF
                </Button>
              </a>
              <Button
                data-testid="spore-clear-btn"
                variant="ghost"
                size="sm"
                onClick={clearAll}
                disabled={totalChecked === 0}
              >
                Clear all ticks
              </Button>
            </div>

            <div className="flex items-center gap-3">
              <Badge
                variant="outline"
                className="bg-blue-50 text-blue-700 border-blue-300"
                data-testid="spore-count-badge"
              >
                {totalChecked} / {FLAT.length} marked
              </Badge>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
                <Input
                  data-testid="spore-search"
                  placeholder="Search symptoms or number…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="pl-8 w-64"
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Grid */}
      <main className="container mx-auto px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {CATEGORIES.map((cat) => {
            const start = startIndex[cat.id];
            const Icon = cat.Icon;
            const visibleItems = cat.items
              .map((label, i) => ({ num: start + i, label }))
              .filter(({ num, label }) => matchesQuery(num, label));

            if (visibleItems.length === 0) return null;

            return (
              <section
                key={cat.id}
                data-testid={`spore-category-${cat.id}`}
                className="bg-white border border-blue-200 rounded-xl shadow-sm overflow-hidden"
              >
                {/* Title bar */}
                <div className="flex items-center justify-between bg-blue-50 px-4 py-3 border-b border-blue-100">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-7 h-7 rounded-full bg-blue-500 text-white font-bold text-sm flex items-center justify-center shrink-0">
                      {cat.id}
                    </div>
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon className="h-4 w-4 text-blue-700 shrink-0" />
                      <h3 className="font-bold text-blue-900 truncate text-sm">
                        {cat.title}
                      </h3>
                    </div>
                  </div>
                  <span className="text-[11px] font-bold text-white bg-blue-500 rounded-full px-2 py-0.5 shrink-0">
                    {cat.range}
                  </span>
                </div>

                {/* Items */}
                <ul className="p-3 space-y-1.5">
                  {visibleItems.map(({ num, label }) => {
                    const isChecked = !!checked[num];
                    return (
                      <li
                        key={num}
                        className="flex items-start gap-3 group cursor-pointer rounded-md px-2 py-1.5 hover:bg-blue-50/50 transition-colors"
                        onClick={() => toggle(num)}
                        data-testid={`spore-item-${num}`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggle(num)}
                          className="mt-1 h-4 w-4 accent-blue-600 cursor-pointer"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="text-[11px] font-bold text-slate-500 tabular-nums mt-0.5 w-8 shrink-0">
                          {String(num).padStart(3, "0")}
                        </span>
                        <span
                          className={`text-sm leading-snug ${
                            isChecked
                              ? "line-through text-slate-400"
                              : "text-slate-800"
                          }`}
                        >
                          {label}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>

        {/* Footer bar */}
        <div className="mt-10 border-t border-blue-200 pt-5">
          <div className="flex flex-wrap justify-center gap-x-10 gap-y-2 text-sm font-bold text-slate-500">
            <span>RESEARCH</span>
            <span className="text-blue-500">•</span>
            <span>OBSERVE</span>
            <span className="text-blue-500">•</span>
            <span>ANALYSE</span>
            <span className="text-blue-500">•</span>
            <span>INFORM</span>
          </div>
          <p className="text-center text-xs italic text-red-700 mt-3">
            Issued by Project SP-ORE Oversight — unauthorised reproduction prohibited.
            All listed indicators associated with confirmed lethal exposure outcomes.
          </p>
        </div>
      </main>
    </div>
  );
};

export default SporeIndex;
